import { Component } from '@theme/component';

/**
 * Custom element for video gallery with hover-to-play functionality
 * @extends {Component}
 */
export class VideoGalleryComponent extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();

    // Get all video items
    this.videoItems = Array.from(this.querySelectorAll('[data-video-item]'));
    
    // Setup hover handlers for each video item
    this.videoItems.forEach((item) => {
      this.setupVideoItem(item);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Clean up event listeners
    this.videoItems?.forEach((item) => {
      const videoElement = item.querySelector('.video-item__video');
      if (videoElement) {
        videoElement.removeEventListener('loadeddata', this.handleVideoLoaded);
        videoElement.removeEventListener('play', this.handleVideoPlay);
        videoElement.removeEventListener('pause', this.handleVideoPause);
      }
    });
  }

  /**
   * Setup hover and interaction handlers for a video item
   * @param {HTMLElement} item - The video item element
   */
  setupVideoItem(item) {
    let hoverTimeout;
    let isHovering = false;
    let videoElement = null;
    let iframe = null;

    // Handle mouse enter
    const handleMouseEnter = () => {
      isHovering = true;
      
      // Small delay to ensure smooth transition
      hoverTimeout = setTimeout(() => {
        if (!isHovering) return;
        
        // Load video if not already loaded
        if (!item.hasAttribute('data-video-loaded')) {
          this.loadVideo(item);
          // Wait a bit for video to load before playing
          setTimeout(() => {
            if (isHovering) {
              this.playVideo(item);
            }
          }, 200);
        } else {
          // Play video immediately if already loaded
          this.playVideo(item);
        }
      }, 150);
    };

    // Handle mouse leave
    const handleMouseLeave = () => {
      isHovering = false;
      
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      
      // Pause video
      this.pauseVideo(item);
    };

    // Add event listeners
    item.addEventListener('mouseenter', handleMouseEnter);
    item.addEventListener('mouseleave', handleMouseLeave);
    
    // For touch devices, handle touch events
    item.addEventListener('touchstart', () => {
      if (!item.hasAttribute('data-video-loaded')) {
        this.loadVideo(item);
      }
      this.playVideo(item);
    });
  }

  /**
   * Load video content from template
   * @param {HTMLElement} item - The video item element
   */
  loadVideo(item) {
    if (item.hasAttribute('data-video-loaded')) return;
    
    const template = item.querySelector('.video-item__template');
    if (!template) return;

    const content = template.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    // Add to DOM
    item.appendChild(content);
    item.setAttribute('data-video-loaded', 'true');

    // Get the video element (could be video tag or iframe)
    // Note: external_video_tag returns an iframe, not a wrapper
    const videoElement = item.querySelector('iframe.video-item__video, video.video-item__video');
    if (videoElement) {
      videoElement.setAttribute('data-loaded', 'true');
      
      // For iframes (YouTube/Vimeo), we need to wait for them to load
      if (videoElement.tagName === 'IFRAME') {
        videoElement.addEventListener('load', () => {
          // Iframe is ready
        }, { once: true });
      } else if (videoElement.tagName === 'VIDEO') {
        // For native video, preload
        videoElement.load();
      }
    }
  }

  /**
   * Play video
   * @param {HTMLElement} item - The video item element
   */
  playVideo(item) {
    const videoElement = item.querySelector('iframe.video-item__video, video.video-item__video');
    if (!videoElement) return;

    // Set playing state immediately for smooth UI transition
    item.setAttribute('data-playing', 'true');

    // Handle different video types
    if (videoElement.tagName === 'IFRAME') {
      // YouTube or Vimeo
      const videoType = videoElement.getAttribute('data-video-type');
      
      if (videoType === 'youtube') {
        // Try to play YouTube video
        try {
          videoElement.contentWindow?.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            '*'
          );
        } catch (error) {
          console.warn('YouTube play failed:', error);
        }
      } else if (videoType === 'vimeo') {
        // Try to play Vimeo video
        try {
          videoElement.contentWindow?.postMessage('{"method":"play"}', '*');
        } catch (error) {
          console.warn('Vimeo play failed:', error);
        }
      }
    } else if (videoElement.tagName === 'VIDEO') {
      // Native video element
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn('Video play failed:', error);
          // If play fails, remove playing state
          item.setAttribute('data-playing', 'false');
        });
      }
    }
  }

  /**
   * Pause video
   * @param {HTMLElement} item - The video item element
   */
  pauseVideo(item) {
    const videoElement = item.querySelector('iframe.video-item__video, video.video-item__video');
    if (!videoElement) {
      item.setAttribute('data-playing', 'false');
      return;
    }

    // Remove playing state immediately for smooth UI transition
    item.setAttribute('data-playing', 'false');

    // Handle different video types
    if (videoElement.tagName === 'IFRAME') {
      // YouTube or Vimeo
      const videoType = videoElement.getAttribute('data-video-type');
      
      if (videoType === 'youtube') {
        try {
          videoElement.contentWindow?.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            '*'
          );
        } catch (error) {
          console.warn('YouTube pause failed:', error);
        }
      } else if (videoType === 'vimeo') {
        try {
          videoElement.contentWindow?.postMessage('{"method":"pause"}', '*');
        } catch (error) {
          console.warn('Vimeo pause failed:', error);
        }
      }
    } else if (videoElement.tagName === 'VIDEO') {
      // Native video element
      videoElement.pause();
    }
  }

  handleVideoLoaded = () => {
    // Video loaded callback
  };

  handleVideoPlay = () => {
    // Video play callback
  };

  handleVideoPause = () => {
    // Video pause callback
  };
}

if (!customElements.get('video-gallery-component')) {
  customElements.define('video-gallery-component', VideoGalleryComponent);
}
